/**
 * Kiosk Settings Tab
 * Tab within GeneralSettingsPage for kiosk-specific configuration
 * Split layout: settings sections (left 35%) | live preview (right 65%)
 * Save/Reset buttons managed by GeneralSettingsPage for consistency
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useKioskSettingsStore } from '../../stores/kiosk-settings-store';
import { useKioskPreviewStore } from '../../stores/kiosk-preview-store';
import { CustomizeSection } from '../sections/kiosk/CustomizeSection';
import { GeneralSection } from '../sections/kiosk/GeneralSection';
import { LanguagesSection } from '../sections/kiosk/LanguagesSection';
import { ScreensSection } from '../sections/kiosk/ScreensSection';
import { PaymentSection } from '../sections/kiosk/PaymentSection';
import { KioskPreviewPanel } from '../KioskPreviewPanel';

interface KioskSettingsTabProps {
  operatorId?: number;
}

export const KioskSettingsTab = ({ operatorId: operatorIdProp }: KioskSettingsTabProps) => {
  const { t } = useTranslation('assignEv');
  const selectedOperator = useOperatorStore((state) => state.selectedOperator);
  const {
    settings,
    isLoading,
    isRefreshing,
    error,
    fetchSettings
  } = useKioskSettingsStore();
  const { formFactor, setFormFactor } = useKioskPreviewStore();

  // Accordion state - only one section open at a time
  const [openSection, setOpenSection] = useState<string>('customize'); // First section (Customize) open by default

  // Handle section toggle (accordion behavior)
  const handleSectionToggle = (sectionId: string) => {
    setOpenSection(openSection === sectionId ? '' : sectionId);
  };

  const resolvedOperatorId = operatorIdProp ?? selectedOperator?.operatorId;

  // Fetch settings on component mount
  useEffect(() => {
    if (resolvedOperatorId) {
      fetchSettings(resolvedOperatorId).catch((err) => {
        console.error('[KioskSettingsTab] Failed to fetch:', err);
      });
    }
  }, [resolvedOperatorId, fetchSettings]);

  // First load - show blocking loader
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-gray-500 mx-auto mb-4" />
          <p className="text-gray-700 font-medium">{t('kiosk.loading')}</p>
        </div>
      </div>
    );
  }

  // No settings and not loading - show error
  if (!settings) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center text-gray-600">
          {error || t('kiosk.noSettings')}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Split Layout: Settings (left) | Preview (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] xl:grid-cols-[42%_58%] gap-6 h-[calc(100vh-300px)]">
        {/* Left Pane - Scrollable Settings Sections */}
        <div className="overflow-y-auto space-y-4 bg-gray-100 rounded-lg p-4">
          <CustomizeSection
            isOpen={openSection === 'customize'}
            onToggle={() => handleSectionToggle('customize')}
          />
          <GeneralSection
            isOpen={openSection === 'general'}
            onToggle={() => handleSectionToggle('general')}
          />
          <LanguagesSection
            isOpen={openSection === 'languages'}
            onToggle={() => handleSectionToggle('languages')}
          />
          <ScreensSection
            isOpen={openSection === 'screens'}
            onToggle={() => handleSectionToggle('screens')}
          />
          <PaymentSection
            isOpen={openSection === 'payment'}
            onToggle={() => handleSectionToggle('payment')}
          />
        </div>

        {/* Right Pane - Sticky Preview */}
        <div className="sticky top-0 h-full flex flex-row gap-2">
          {/* Form Factor Toggle - vertical on the left */}
          <div className="flex flex-col justify-start gap-2 pt-4">
            <button
              className={`rounded px-3 py-2 text-sm font-medium transition-colors ${
                formFactor === '4inch'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setFormFactor('4inch')}
            >
              4"
            </button>
            <button
              className={`rounded px-3 py-2 text-sm font-medium transition-colors ${
                formFactor === '5inch'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setFormFactor('5inch')}
            >
              5"
            </button>
          </div>
          <KioskPreviewPanel />
        </div>
      </div>

      {/* Silent Loader - appears when refreshing in background */}
      {isRefreshing && (
        <div className="fixed top-4 right-4 flex items-center gap-2 bg-white px-3 py-2 rounded-lg shadow-md border border-gray-200 z-50">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-gray-500" />
          <p className="text-xs text-gray-600 font-medium">{t('kiosk.updating')}</p>
        </div>
      )}
    </div>
  );
};
